import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const FADE_UP = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6 } };
const SLIDE_IN = { initial: { opacity: 0, x: 20 }, animate: { opacity: 1, x: 0 }, transition: { duration: 0.4 } };

const steps = ["Détails de l'envoi", "Vos coordonnées"];

const conditionnements = ["Palette(s)", "Vrac", "Caisse(s)", "Fût(s)", "Conteneur", "Colis", "Autre"];

const INITIAL_FORM = {
  type_transport: "routier",
  depart: "", code_postal_depart: "", date_souhaitee: "",
  conditionnement: "", quantite: "", type_marchandise: "", particularite: "",
  arrivee: "", code_postal_arrivee: "", date_livraison: "", poids: "",
  nom: "", entreprise: "", email: "", telephone: "",
};

const INPUT_CLS = "w-full bg-white/[0.04] border border-white/10 text-white placeholder-slate-600 px-4 py-3 rounded-xl text-sm focus:border-[#E8902A]/50 transition-all";
const SELECT_CLS = "w-full bg-[#1a2e48] border border-white/10 text-white px-4 py-3 rounded-xl text-sm focus:border-[#E8902A]/50 transition-all appearance-none";

// --- Custom hook ---

function useDevisForm() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(INITIAL_FORM);
  const [status, setStatus] = useState("idle");

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await axios.post(`${API}/devis`, form);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  const reset = () => { setStatus("idle"); setStep(0); setForm(INITIAL_FORM); };

  return { step, setStep, form, status, handleChange, handleSubmit, reset };
}

// --- Sub-components ---

function DevisSuccessScreen({ onReset }) {
  return (
    <div className="min-h-screen bg-[#1a2e48] flex items-center justify-center px-6 pt-20" data-testid="devis-success">
      <div className="text-center max-w-md">
        <div className="w-20 h-20 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-6">
          <CheckCircle size={40} className="text-emerald-400" />
        </div>
        <h2 className="font-heading font-black text-3xl text-white mb-3 uppercase">Demande envoyée !</h2>
        <p className="text-slate-400 leading-relaxed mb-8">
          Merci ! Votre demande de devis a bien été reçue. Notre équipe vous contactera sous 24h avec une proposition personnalisée.
        </p>
        <button onClick={onReset} className="bg-[#E8902A] hover:bg-[#C26218] text-white font-heading font-bold text-sm tracking-widest uppercase px-8 py-4 transition-all duration-300" data-testid="devis-reset-btn">
          Nouvelle demande
        </button>
      </div>
    </div>
  );
}

function DevisStepIndicator({ step }) {
  return (
    <section className="bg-[#152540] py-8 border-b border-white/5">
      <div className="max-w-4xl mx-auto px-6">
        <div className="flex items-center justify-between">
          {steps.map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex items-center gap-2" data-testid={`step-${i}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-heading font-black text-sm transition-all duration-300 ${i <= step ? "bg-[#E8902A] text-white" : "bg-white/10 text-slate-500"}`}>
                  {i + 1}
                </div>
                <span className={`font-heading font-bold text-xs uppercase tracking-widest hidden sm:block transition-colors ${i <= step ? "text-white" : "text-slate-600"}`}>{s}</span>
              </div>
              {i < steps.length - 1 && (
                <div className={`flex-1 h-px mx-4 transition-colors duration-300 ${i < step ? "bg-[#E8902A]" : "bg-white/10"}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

function DevisStep1({ form, handleChange, onNext }) {
  const canContinue = form.depart && form.arrivee && form.code_postal_depart && form.code_postal_arrivee;

  return (
    <motion.div {...SLIDE_IN}>
      <h2 className="font-heading font-black text-2xl text-white mb-2 uppercase">Détails de l'envoi</h2>
      <p className="text-slate-400 text-sm mb-8">Renseignez les informations de départ et de destination.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
        <div className="space-y-5">
          <div>
            <label className="block font-heading font-bold text-xs tracking-widest uppercase text-slate-400 mb-2">Lieu de départ *</label>
            <input type="text" name="depart" value={form.depart} onChange={handleChange} required placeholder="Ville de départ" className={INPUT_CLS} data-testid="devis-depart" />
          </div>
          <div>
            <label className="block font-heading font-bold text-xs tracking-widest uppercase text-slate-400 mb-2">Code postal départ *</label>
            <input type="text" name="code_postal_depart" value={form.code_postal_depart} onChange={handleChange} required placeholder="Ex: 03200" className={INPUT_CLS} data-testid="devis-cp-depart" />
          </div>
          <div>
            <label className="block font-heading font-bold text-xs tracking-widest uppercase text-slate-400 mb-2">Date de départ</label>
            <input type="date" name="date_souhaitee" value={form.date_souhaitee} onChange={handleChange} className={SELECT_CLS} data-testid="devis-date-depart" />
          </div>
          <div>
            <label className="block font-heading font-bold text-xs tracking-widest uppercase text-slate-400 mb-2">Conditionnement</label>
            <select name="conditionnement" value={form.conditionnement} onChange={handleChange} className={SELECT_CLS} data-testid="devis-conditionnement">
              <option value="">Sélectionner...</option>
              {conditionnements.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block font-heading font-bold text-xs tracking-widest uppercase text-slate-400 mb-2">Quantité</label>
            <input type="text" name="quantite" value={form.quantite} onChange={handleChange} placeholder="Ex: 3 palettes, 10 colis..." className={INPUT_CLS} data-testid="devis-quantite" />
          </div>
          <div>
            <label className="block font-heading font-bold text-xs tracking-widest uppercase text-slate-400 mb-2">Type de marchandise</label>
            <input type="text" name="type_marchandise" value={form.type_marchandise} onChange={handleChange} placeholder="Ex: matériaux BTP, alimentation..." className={INPUT_CLS} data-testid="devis-marchandise" />
          </div>
          <div>
            <label className="block font-heading font-bold text-xs tracking-widest uppercase text-slate-400 mb-2">Particularité</label>
            <input type="text" name="particularite" value={form.particularite} onChange={handleChange} placeholder="Ex: fragile, ADR, température dirigée..." className={INPUT_CLS} data-testid="devis-particularite" />
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <label className="block font-heading font-bold text-xs tracking-widest uppercase text-slate-400 mb-2">Destination *</label>
            <input type="text" name="arrivee" value={form.arrivee} onChange={handleChange} required placeholder="Ville de livraison" className={INPUT_CLS} data-testid="devis-arrivee" />
          </div>
          <div>
            <label className="block font-heading font-bold text-xs tracking-widest uppercase text-slate-400 mb-2">Code postal destination *</label>
            <input type="text" name="code_postal_arrivee" value={form.code_postal_arrivee} onChange={handleChange} required placeholder="Ex: 69001" className={INPUT_CLS} data-testid="devis-cp-arrivee" />
          </div>
          <div>
            <label className="block font-heading font-bold text-xs tracking-widest uppercase text-slate-400 mb-2">Date de livraison souhaitée</label>
            <input type="date" name="date_livraison" value={form.date_livraison} onChange={handleChange} className={SELECT_CLS} data-testid="devis-date-livraison" />
          </div>
          <div>
            <label className="block font-heading font-bold text-xs tracking-widest uppercase text-slate-400 mb-2">Poids en kg</label>
            <input type="text" name="poids" value={form.poids} onChange={handleChange} placeholder="Ex: 500 kg" className={INPUT_CLS} data-testid="devis-poids" />
          </div>
        </div>
      </div>

      <div className="mt-8">
        <button
          type="button"
          onClick={onNext}
          disabled={!canContinue}
          className="flex items-center gap-2 bg-[#E8902A] hover:bg-[#C26218] disabled:opacity-40 disabled:cursor-not-allowed text-white font-heading font-bold text-sm tracking-widest uppercase px-8 py-4 transition-all duration-300 hover:-translate-y-0.5"
          data-testid="devis-next-step1"
        >
          Continuer <ArrowRight size={16} />
        </button>
      </div>
    </motion.div>
  );
}

function DevisStep2({ form, handleChange, onBack, onSubmit, status }) {
  return (
    <motion.div {...SLIDE_IN}>
      <h2 className="font-heading font-black text-2xl text-white mb-2 uppercase">Vos coordonnées</h2>
      <p className="text-slate-400 text-sm mb-8">Pour vous envoyer votre devis personnalisé.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="block font-heading font-bold text-xs tracking-widest uppercase text-slate-400 mb-2">Nom complet *</label>
          <input type="text" name="nom" value={form.nom} onChange={handleChange} required placeholder="Jean Dupont" className={INPUT_CLS} data-testid="devis-nom" />
        </div>
        <div>
          <label className="block font-heading font-bold text-xs tracking-widest uppercase text-slate-400 mb-2">Entreprise</label>
          <input type="text" name="entreprise" value={form.entreprise} onChange={handleChange} placeholder="Votre société" className={INPUT_CLS} data-testid="devis-entreprise" />
        </div>
        <div>
          <label className="block font-heading font-bold text-xs tracking-widest uppercase text-slate-400 mb-2">Email *</label>
          <input type="email" name="email" value={form.email} onChange={handleChange} required placeholder="jean@entreprise.fr" className={INPUT_CLS} data-testid="devis-email" />
        </div>
        <div>
          <label className="block font-heading font-bold text-xs tracking-widest uppercase text-slate-400 mb-2">Téléphone</label>
          <input type="tel" name="telephone" value={form.telephone} onChange={handleChange} placeholder="+33 4 70 XX XX XX" className={INPUT_CLS} data-testid="devis-telephone" />
        </div>
      </div>

      {status === "error" && (
        <div className="mt-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="devis-error">
          Une erreur est survenue. Veuillez réessayer.
        </div>
      )}

      <div className="flex gap-4 mt-8">
        <button type="button" onClick={onBack} className="border border-white/15 text-slate-400 hover:text-white hover:border-white/30 font-heading font-bold text-sm tracking-widest uppercase px-6 py-4 transition-all duration-300" data-testid="devis-back-step2">
          Retour
        </button>
        <button
          type="submit"
          onClick={onSubmit}
          disabled={status === "loading" || !form.nom || !form.email}
          className="flex items-center gap-2 bg-[#E8902A] hover:bg-[#C26218] disabled:opacity-40 disabled:cursor-not-allowed text-white font-heading font-bold text-sm tracking-widest uppercase px-8 py-4 transition-all duration-300 shadow-[0_4px_20px_rgba(232,144,42,0.4)] hover:-translate-y-0.5"
          data-testid="devis-submit"
        >
          {status === "loading" ? "Envoi en cours..." : "Envoyer ma demande"}
          {status !== "loading" && <ArrowRight size={16} />}
        </button>
      </div>
    </motion.div>
  );
}

// --- Page principale ---

export default function Devis() {
  const { step, setStep, form, status, handleChange, handleSubmit, reset } = useDevisForm();

  if (status === "success") {
    return <DevisSuccessScreen onReset={reset} />;
  }

  return (
    <div data-testid="devis-page">
      <Helmet>
        <title>Demande de devis — Pierre Affrètement | Transport & Fret</title>
        <link rel="canonical" href="https://pierreaffretement.fr/devis" />
        <meta name="description" content="Demandez un devis personnalisé pour votre transport routier ou fret maritime. Réponse sous 24h par Pierre Affrètement, Vichy (03)." />
      </Helmet>

      {/* Hero */}
      <section className="relative py-24 pt-36 bg-[#1a2e48] overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/hero-auvergne-bg.jpeg" alt="Devis Transport" className="w-full h-full object-cover opacity-[0.93]" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a2e48]/20 to-[#1a2e48]" />
        </div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-64 bg-[#E8902A]/5 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <motion.div {...FADE_UP}>
            <div className="flex items-center justify-center gap-3 mb-5">
              <div className="h-px w-10 bg-[#E8902A]" />
              <span className="font-heading font-bold text-xs tracking-widest uppercase text-[#E8902A]">Transport Routier — Devis gratuit</span>
              <div className="h-px w-10 bg-[#E8902A]" />
            </div>
            <h1 className="font-heading font-black text-4xl md:text-5xl text-white leading-tight uppercase mb-3">
              Obtenez votre <span className="gradient-text">devis</span>
            </h1>
            <p className="text-slate-400 text-base mb-4">Réponse personnalisée sous 24h. Sans engagement.</p>
          </motion.div>
        </div>
      </section>

      <DevisStepIndicator step={step} />

      {/* Form */}
      <section className="py-16 bg-[#152540]">
        <div className="max-w-4xl mx-auto px-6">
          <form onSubmit={handleSubmit} data-testid="devis-form">
            {step === 0 && (
              <DevisStep1 form={form} handleChange={handleChange} onNext={() => setStep(1)} />
            )}
            {step === 1 && (
              <DevisStep2 form={form} handleChange={handleChange} onBack={() => setStep(0)} onSubmit={handleSubmit} status={status} />
            )}
          </form>
        </div>
      </section>
    </div>
  );
}
